/**
 * INTEROPERABILITY VIEW - SIH Standards Browser
 * Displays CIM-10, ATC, and LOINC mappings stored in MongoDB.
 * Allows Super Admins to edit mappings and export patient data in FHIR R4 format.
 */

import { useState, useEffect, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  Globe, Search, Download, RefreshCw, FileText, Pill, Activity,
  ChevronDown, ChevronUp, Edit3, Check, X, Loader2, Stethoscope,
  FlaskConical, Heart, BarChart3, Server, ArrowLeftRight, AlertTriangle
} from "lucide-react";

const SYSTEM_TABS = [
  { id: "cim10", label: "CIM-10 (Diagnostics)", icon: Stethoscope, color: "bg-rose-500" },
  { id: "atc", label: "ATC (Molecules)", icon: Pill, color: "bg-indigo-500" },
  { id: "loinc", label: "LOINC (Biologie/Vitaux)", icon: Activity, color: "bg-emerald-500" },
];

export default function InteroperabilityView() {
  const [mappings, setMappings] = useState({ cim10: [], atc: [], loinc: [] });
  const [stats, setStats] = useState(null);
  const [sihAudit, setSihAudit] = useState(null);
  const [activeSystem, setActiveSystem] = useState("cim10");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [exportingPdf, setExportingPdf] = useState(null);
  const [exportingFhir, setExportingFhir] = useState(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const res = await api.get(`/interop/mappings?${params.toString()}`);
      setMappings(res.data);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error("Erreur chargement des mappings");
      }
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/interop/stats");
      setStats(res.data);
    } catch {
      // stats optional
    }
  }, []);

  const fetchSihAudit = useCallback(async () => {
    try {
      const res = await api.get("/interop/sih/audit");
      setSihAudit(res.data);
    } catch {
      setSihAudit(null);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
    fetchStats();
    fetchSihAudit();
  }, [fetchMappings, fetchStats, fetchSihAudit]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await api.post("/interop/seed");
      toast.success("Mappings initialises avec succes");
      fetchMappings();
      fetchStats();
      fetchSihAudit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du seeding");
    } finally {
      setSeeding(false);
    }
  };

  const handleSearch = () => {
    fetchMappings();
  };

  const startEdit = (row) => {
    setEditingRow(row.hana_label);
    setEditData({ ...row });
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const saveEdit = async () => {
    try {
      await api.put(`/interop/mappings/${activeSystem}/${editingRow}`, editData);
      toast.success("Mapping mis a jour");
      setEditingRow(null);
      fetchMappings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de mise a jour");
    }
  };

  const searchPatients = async () => {
    if (!patientSearch || patientSearch.length < 2) return;
    try {
      const res = await api.get(`/interop/patients/search?q=${encodeURIComponent(patientSearch)}`);
      setPatients(res.data || []);
    } catch {
      toast.error("Erreur recherche patients");
    }
  };

  const downloadPdf = async (patientId, patientName) => {
    setExportingPdf(patientId);
    try {
      const res = await api.get(`/interop/patient/${patientId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `HANA_Interop_${patientName}_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF telecharge");
    } catch (err) {
      toast.error("Erreur generation PDF");
    } finally {
      setExportingPdf(null);
    }
  };

  const downloadFhir = async (patientId, patientName) => {
    setExportingFhir(patientId);
    try {
      const res = await api.get(`/interop/patient/${patientId}/fhir?anonymous=true`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `HANA_FHIR_${patientName}_${new Date().toISOString().split("T")[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("FHIR Bundle telecharge");
    } catch (err) {
      toast.error("Erreur generation FHIR");
    } finally {
      setExportingFhir(null);
    }
  };

  const currentData = mappings[activeSystem] || [];
  const isEmpty = !currentData.length && !loading;

  return (
    <div className="space-y-6 mt-4" data-testid="interop-view">
      {/* Header + Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Interoperabilite SIH</h2>
            <p className="text-xs text-slate-500">Mappings CIM-10, ATC, LOINC pour integration hospitaliere</p>
          </div>
        </div>
        {isEmpty && (
          <button
            onClick={handleSeed}
            disabled={seeding}
            data-testid="seed-interop-btn"
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 disabled:opacity-50"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Initialiser les mappings
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="interop-stats">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Stethoscope className="w-4 h-4 text-rose-500" />
              <span className="text-xs font-medium text-slate-500">CIM-10</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.mappings?.cim10?.total || 0}</p>
            <p className="text-xs text-slate-400">types de cancer codes</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Pill className="w-4 h-4 text-indigo-500" />
              <span className="text-xs font-medium text-slate-500">ATC</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.mappings?.atc?.total || 0}</p>
            <p className="text-xs text-slate-400">molecules codees</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-slate-500">LOINC</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.mappings?.loinc?.total || 0}</p>
            <p className="text-xs text-slate-400">observations codees</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-cyan-500" />
              <span className="text-xs font-medium text-slate-500">Couverture CIM-10</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.coverage?.coverage_rate_cim10 || 0}%</p>
            <p className="text-xs text-slate-400">{stats.coverage?.patients_with_cim10 || 0} patients mappes</p>
          </div>
        </div>
      )}

      {sihAudit && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="sih-readiness-panel">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-slate-800">Audit SIH réel</h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${sihAudit.isFullySIHInteroperated ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                  data-testid="sih-readiness-verdict"
                >
                  {sihAudit.isFullySIHInteroperated ? "Interopéré SIH" : "Socle partiel, non connecté"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1" data-testid="sih-readiness-positioning">
                {sihAudit.honestPositioning}
              </p>
            </div>
            <button
              type="button"
              onClick={fetchSihAudit}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="sih-readiness-refresh-button"
            >
              <RefreshCw className="w-4 h-4" /> Rafraîchir
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <SihStatusCard icon={BarChart3} title="Mappings" component={sihAudit.components?.semantic_mappings} testid="sih-component-mappings" />
            <SihStatusCard icon={Globe} title="FHIR" component={sihAudit.components?.fhir_export_and_read_api} testid="sih-component-fhir" />
            <SihStatusCard icon={Server} title="HL7 v2 / MLLP" component={sihAudit.components?.hl7_v2_mllp} testid="sih-component-hl7" />
            <SihStatusCard icon={FileText} title="SFTP CSV/XML" component={sihAudit.components?.sftp_csv_xml} testid="sih-component-sftp" />
            <SihStatusCard icon={ArrowLeftRight} title="Bidirectionnel" component={sihAudit.components?.bidirectional} testid="sih-component-bidirectional" />
          </div>
          {!sihAudit.isFullySIHInteroperated && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="sih-next-required">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Pour devenir réellement interopéré SIH</p>
                  <p className="text-xs text-amber-800 mt-1">
                    {sihAudit.nextRequiredFromClinic?.slice(0, 3).join(" · ")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {SYSTEM_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveSystem(tab.id); setSearch(""); }}
            data-testid={`interop-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSystem === tab.id
                ? "bg-slate-800 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Rechercher par label, code ou description..."
            data-testid="interop-search"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        <button onClick={handleSearch} className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm">
          <Search className="w-4 h-4" />
        </button>
        <button onClick={() => { setSearch(""); fetchMappings(); }} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          <span className="ml-2 text-sm text-slate-500">Chargement...</span>
        </div>
      )}

      {/* Table */}
      {!loading && currentData.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="interop-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {activeSystem === "cim10" && (
                    <>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Label HANA</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Code CIM-10</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Description</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Categorie</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">Actions</th>
                    </>
                  )}
                  {activeSystem === "atc" && (
                    <>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Molecule (HANA)</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">DCI</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Code ATC</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Classe</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Categorie</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">Actions</th>
                    </>
                  )}
                  {activeSystem === "loinc" && (
                    <>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Label HANA</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Code LOINC</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Description</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Unite</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Composant</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentData.map((row, idx) => {
                  const isEditing = editingRow === row.hana_label;
                  return (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`interop-row-${idx}`}>
                      {activeSystem === "cim10" && (
                        <>
                          <td className="px-4 py-3 font-medium text-slate-800">{row.hana_label}</td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input
                                value={editData.code_specific || ""}
                                onChange={e => setEditData({...editData, code_specific: e.target.value})}
                                className="border rounded px-2 py-1 text-sm w-24"
                              />
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-rose-100 text-rose-700">{row.code_specific || row.code}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{row.display_fr}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${row.category === "Hemopathies malignes" ? "bg-purple-100 text-purple-700" : "bg-amber-100 text-amber-700"}`}>
                              {row.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isEditing ? (
                              <div className="flex gap-1 justify-center">
                                <button onClick={saveEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-4 h-4" /></button>
                                <button onClick={cancelEdit} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                              </div>
                            ) : (
                              <button onClick={() => startEdit(row)} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" data-testid={`edit-cim10-${idx}`}>
                                <Edit3 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </>
                      )}
                      {activeSystem === "atc" && (
                        <>
                          <td className="px-4 py-3 font-medium text-slate-800 max-w-[200px] truncate">{row.hana_label}</td>
                          <td className="px-4 py-3 text-slate-600">{row.dci}</td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input
                                value={editData.code || ""}
                                onChange={e => setEditData({...editData, code: e.target.value})}
                                className="border rounded px-2 py-1 text-sm w-28"
                              />
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-indigo-100 text-indigo-700">{row.code}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{row.class_name}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{row.category}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isEditing ? (
                              <div className="flex gap-1 justify-center">
                                <button onClick={saveEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-4 h-4" /></button>
                                <button onClick={cancelEdit} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                              </div>
                            ) : (
                              <button onClick={() => startEdit(row)} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" data-testid={`edit-atc-${idx}`}>
                                <Edit3 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </>
                      )}
                      {activeSystem === "loinc" && (
                        <>
                          <td className="px-4 py-3 font-medium text-slate-800">{row.hana_label}</td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input
                                value={editData.code || ""}
                                onChange={e => setEditData({...editData, code: e.target.value})}
                                className="border rounded px-2 py-1 text-sm w-24"
                              />
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-emerald-100 text-emerald-700">{row.code}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{row.display_fr}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 font-mono">{row.unit}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${row.component === "vitals" ? "bg-cyan-100 text-cyan-700" : "bg-amber-100 text-amber-700"}`}>
                              {row.component === "vitals" ? "Constantes" : "Biologie"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isEditing ? (
                              <div className="flex gap-1 justify-center">
                                <button onClick={saveEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-4 h-4" /></button>
                                <button onClick={cancelEdit} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                              </div>
                            ) : (
                              <button onClick={() => startEdit(row)} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" data-testid={`edit-loinc-${idx}`}>
                                <Edit3 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            {currentData.length} enregistrement(s) affiches
          </div>
        </div>
      )}

      {/* Patient Export Panel */}
      {!isEmpty && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="patient-export-panel">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-cyan-600" />
            <h3 className="text-sm font-bold text-slate-800">Export Patient Standardise</h3>
            <span className="text-xs text-slate-400 ml-2">PDF avec codes CIM-10 / ATC / LOINC + FHIR R4 JSON</span>
          </div>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={patientSearch}
              onChange={e => setPatientSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && searchPatients()}
              placeholder="Rechercher un patient par nom..."
              data-testid="patient-export-search"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <button
              onClick={searchPatients}
              className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm"
              data-testid="patient-search-btn"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
          {patients.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {patients.map(p => (
                <div key={p.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3" data-testid={`patient-row-${p.id}`}>
                  <div>
                    <span className="text-sm font-medium text-slate-800">{p.firstName} {p.lastName}</span>
                    {p.cancerType && <span className="text-xs text-slate-400 ml-3">{p.cancerType}</span>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadPdf(p.id, `${p.lastName}_${p.firstName}`)}
                      disabled={exportingPdf === p.id}
                      data-testid={`export-pdf-${p.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 disabled:opacity-50"
                    >
                      {exportingPdf === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                      PDF
                    </button>
                    <button
                      onClick={() => downloadFhir(p.id, `${p.lastName}_${p.firstName}`)}
                      disabled={exportingFhir === p.id}
                      data-testid={`export-fhir-${p.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium hover:bg-indigo-100 disabled:opacity-50"
                    >
                      {exportingFhir === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      FHIR R4
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="interop-empty">
          <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600 mb-2">Aucun mapping trouve</h3>
          <p className="text-sm text-slate-400 mb-6">
            Cliquez sur "Initialiser les mappings" pour charger les dictionnaires CIM-10, ATC et LOINC dans la base.
          </p>
        </div>
      )}
    </div>
  );
}

function SihStatusCard({ icon: Icon, title, component, testid }) {
  const status = component?.status || "unknown";
  const ok = status === "ready";
  const warn = ["partial", "configured_not_exposed"].includes(status);
  const cls = ok
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : warn
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-rose-200 bg-rose-50 text-rose-700";
  return (
    <div className="border border-slate-200 rounded-lg p-3 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-medium text-slate-600 truncate">{title}</span>
      </div>
      <span className={`inline-flex text-[11px] font-semibold rounded-full px-2 py-0.5 border ${cls}`} data-testid={`${testid}-status`}>
        {status}
      </span>
      <p className="text-xs text-slate-500 mt-2 line-clamp-3" data-testid={`${testid}-evidence`}>
        {component?.evidence || "Aucune donnée"}
      </p>
    </div>
  );
}
